import { FC, useEffect, useState } from 'react';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { theme } from '@store';

interface INumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

export const NumberStepper: FC<INumberStepperProps> = ({
  value,
  onChange,
  label,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  disabled = false,
}) => {
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  const [inputValue, setInputValue] = useState(String(value));

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleDecrement = () => onChange(clamp(value - step));
  const handleIncrement = () => onChange(clamp(value + step));

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === '' || /^-?\d*$/.test(raw)) {
      setInputValue(raw);
    }
  };

  const commitInputValue = () => {
    const parsed = Number(inputValue);
    const next =
      inputValue === '' || Number.isNaN(parsed) ? value : clamp(parsed);
    setInputValue(String(next));
    if (next !== value) {
      onChange(next);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commitInputValue();
    }
  };

  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || value >= max;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
      }}
    >
      {label && (
        <Typography
          variant='body1'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&::before': {
              content: '""',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: theme.palette.common.black,
            },
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 40,
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <IconButton
          size='small'
          onClick={handleDecrement}
          disabled={isDecrementDisabled}
          sx={{
            borderRadius: 0,
            px: 1,
            alignSelf: 'stretch',
            color: theme.palette.semiBlack.main,
            backgroundColor: 'rgba(245, 245, 245, 1)',
          }}
        >
          <Remove fontSize='small' />
        </IconButton>

        <Box
          sx={{
            width: 60,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            borderLeft: `1px solid ${theme.palette.grey[300]}`,
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: disabled
              ? 'rgba(245, 245, 245, 1)'
              : 'transparent',
          }}
        >
          <InputBase
            value={inputValue}
            onChange={handleInputChange}
            onBlur={commitInputValue}
            onKeyDown={handleInputKeyDown}
            disabled={disabled}
            inputProps={{
              inputMode: 'numeric',
              'aria-label': label,
              style: { textAlign: 'center', padding: 0, width: '100%' },
            }}
            sx={{
              px: 0.5,
              py: 0.75,
              width: '100%',
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 500,
              color: theme.palette.semiBlack.main,
            }}
          />
        </Box>

        <IconButton
          size='small'
          onClick={handleIncrement}
          disabled={isIncrementDisabled}
          sx={{
            borderRadius: 0,
            px: 1,
            alignSelf: 'stretch',
            color: theme.palette.semiBlack.main,
            backgroundColor: 'rgba(245, 245, 245, 1)',
          }}
        >
          <Add fontSize='small' />
        </IconButton>
      </Box>
    </Box>
  );
};
